import { useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar, type CalendarProps } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { businessTodayDate, formatLocalDate, parseLocalDate } from "@/lib/local-date";
import { cn } from "@/lib/utils";

type LocalDatePickerProps = {
  /** Local date string in YYYY-MM-DD format (or "" for empty). */
  value: string;
  /** Called with the next YYYY-MM-DD value (or "" when cleared). */
  onChange: (next: string) => void;
  placeholder?: string;
  /** Trigger text for a picked date. Defaults to M/d/yyyy. */
  formatLabel?: (date: Date) => string;
  allowClear?: boolean;
  buttonSize?: "default" | "sm" | "lg";
  buttonClassName?: string;
  buttonAriaLabel?: string;
  calendarProps?: Omit<
    CalendarProps,
    "mode" | "selected" | "defaultMonth" | "onSelect"
  >;
  /** Open the calendar as soon as the picker mounts (editing a value in place). */
  defaultOpen?: boolean;
  /** Called when the user closes the calendar without picking (Esc, click outside). */
  onDismiss?: () => void;
  /** A "Today" button under the calendar; today is the Eastern business date. */
  showToday?: boolean;
  disabled?: boolean;
};

export function LocalDatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  formatLabel = (date) => format(date, "M/d/yyyy"),
  allowClear = false,
  buttonSize = "lg",
  buttonClassName,
  buttonAriaLabel,
  calendarProps,
  defaultOpen = false,
  onDismiss,
  showToday = false,
  disabled = false,
}: LocalDatePickerProps) {
  const [open, setOpen] = useState(defaultOpen);
  const selected = value ? parseLocalDate(value) : undefined;
  const pick = (next: string) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <div className="flex items-center gap-2">
      {/* `modal` so the picker works correctly when used inside a Dialog/
          Sheet — see gotcha #11 in .claude/skills/ux-polish/gotchas.md. Safe
          for non-Sheet contexts: a date-picker calendar is short-lived and
          expects to own focus while open. */}
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) onDismiss?.();
        }}
        modal
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            aria-label={buttonAriaLabel}
            disabled={disabled}
            variant="outline"
            size={buttonSize}
            className={cn(
              "flex-1 min-w-0 justify-start text-left font-normal",
              !value && "text-muted-foreground",
              buttonClassName,
            )}
          >
            <CalendarIcon className="h-4 w-4" />
            {selected ? formatLabel(selected) : placeholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selected}
            defaultMonth={selected}
            initialFocus
            onSelect={(date) => {
              if (!date) return;
              pick(formatLocalDate(date));
            }}
            {...calendarProps}
          />
          {showToday && (
            <div className="flex items-center justify-between gap-2 border-t p-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => pick(formatLocalDate(businessTodayDate()))}>
                Today
              </Button>
              {allowClear && value ? (
                <Button type="button" variant="ghost" size="sm" onClick={() => pick("")}>
                  Clear
                </Button>
              ) : null}
            </div>
          )}
        </PopoverContent>
      </Popover>

      {allowClear && value ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn(buttonSize === "lg" ? "h-10 w-10" : undefined)}
          disabled={disabled}
          onClick={() => onChange("")}
        >
          <X className="h-4 w-4" />
        </Button>
      ) : null}
    </div>
  );
}
